import { Injectable } from '@angular/core';

export interface UploadLogEntry {
  timestamp: Date;
  fileName: string;
  fileSize: number;
  fileType: string;
  uploadType: string;
  status: 'started' | 'success' | 'failed' | 'validation_failed';
  error?: string;
  duration?: number;
  url?: string;
}

@Injectable({
  providedIn: 'root'
})
export class UploadLoggingService {
  private uploadLogs: UploadLogEntry[] = [];
  private readonly MAX_LOGS = 100; // Keep last 100 upload attempts

  /**
   * Log upload start
   */
  logUploadStart(fileName: string, fileSize: number, fileType: string, uploadType: string): string {
    const logId = this.generateLogId();
    const entry: UploadLogEntry = {
      timestamp: new Date(),
      fileName,
      fileSize,
      fileType,
      uploadType,
      status: 'started'
    };
    
    this.addLogEntry(entry);
    console.log(`Upload started [${logId}]:`, entry);
    return logId;
  }

  /**
   * Log upload success
   */
  logUploadSuccess(logId: string, url: string, duration: number): void {
    const entry = this.findLogEntry(logId);
    if (entry) {
      entry.status = 'success';
      entry.url = url;
      entry.duration = duration;
      console.log(`Upload successful [${logId}]:`, entry);
    }
  }

  /**
   * Log upload failure
   */
  logUploadFailure(logId: string, error: string, duration: number): void {
    const entry = this.findLogEntry(logId);
    if (entry) {
      entry.status = 'failed';
      entry.error = error;
      entry.duration = duration;
      console.error(`Upload failed [${logId}]:`, entry);
    }
  }

  /**
   * Log validation failure
   */
  logValidationFailure(fileName: string, fileSize: number, fileType: string, uploadType: string, errors: string[]): void {
    const entry: UploadLogEntry = {
      timestamp: new Date(),
      fileName,
      fileSize,
      fileType,
      uploadType,
      status: 'validation_failed',
      error: errors.join(', ')
    };
    
    this.addLogEntry(entry);
    console.warn(`Upload validation failed:`, entry);
  }

  /**
   * Get recent upload logs
   */
  getRecentLogs(limit: number = 20): UploadLogEntry[] {
    return this.uploadLogs
      .slice(-limit)
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }

  /**
   * Get upload statistics
   */
  getUploadStats(): {
    total: number;
    successful: number;
    failed: number;
    validationFailed: number;
    successRate: number;
    averageDuration: number;
  } {
    const total = this.uploadLogs.length;
    const successful = this.uploadLogs.filter(log => log.status === 'success').length;
    const failed = this.uploadLogs.filter(log => log.status === 'failed').length;
    const validationFailed = this.uploadLogs.filter(log => log.status === 'validation_failed').length;
    
    const successRate = total > 0 ? (successful / total) * 100 : 0;
    
    const completedUploads = this.uploadLogs.filter(log => log.duration !== undefined);
    const averageDuration = completedUploads.length > 0 
      ? completedUploads.reduce((sum, log) => sum + (log.duration || 0), 0) / completedUploads.length 
      : 0;

    return {
      total,
      successful,
      failed,
      validationFailed,
      successRate: Math.round(successRate * 100) / 100,
      averageDuration: Math.round(averageDuration)
    };
  }

  /**
   * Clear old logs
   */
  clearOldLogs(): void {
    if (this.uploadLogs.length > this.MAX_LOGS) {
      this.uploadLogs = this.uploadLogs.slice(-this.MAX_LOGS);
    }
  }

  private generateLogId(): string {
    return `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private addLogEntry(entry: UploadLogEntry): void {
    this.uploadLogs.push(entry);
    this.clearOldLogs();
  }

  private findLogEntry(logId: string): UploadLogEntry | undefined {
    // Since we don't store logId in the entry, we'll find by timestamp and fileName
    // This is a simplified approach - in a real implementation, you'd store the logId
    return this.uploadLogs[this.uploadLogs.length - 1];
  }
}
